import { trackClipDownloaded } from '@src/analytics/EventTracker';
import { err, ok, type Result } from '@src/services/common/Result';
import { sanitizeFileName } from '@src/utility/filename';
import { downloadMedia } from '@src/utility/general';
import { LogManager } from '@src/utility/Logger';
import { format } from 'date-fns';

import { extractErrorInfo } from '../utility/error';

const logger = LogManager.getLogger('service:VideoDownload');

export interface VideoDownloadParams {
  url: string;
  /**
   * Pre-sanitized file name (no extension). When provided, it is used verbatim
   * — `downloadVideo` does NOT sanitize it — bypassing the default
   * `{storyTitle} - {videoTitle} - {timestamp}` naming. Callers with their own
   * naming convention (e.g. per-variant `{title}_{lang}_{ar}_{res}`) pass it
   * here already run through `sanitizeFileName`.
   */
  sanitizedFileName?: string;
  metadata: {
    clipId?: string;
    storyId?: string;
    storyTitle?: string;
    videoTitle?: string;
    aspectRatio?: string;
    sourcePlatform: string;
  };
}

function buildVideoFileName(storyTitle?: string, videoTitle?: string): string {
  const raw = `${storyTitle || 'Untitled Story'} - ${
    videoTitle || 'Untitled Video'
  } - ${format(new Date(), 'yyyy-MM-dd HH-mm-ss')}`;
  return sanitizeFileName(raw);
}

export async function downloadVideo(
  params: VideoDownloadParams,
): Promise<Result<void>> {
  try {
    trackClipDownloaded({
      clip_id: params.metadata.clipId,
      story_id: params.metadata.storyId,
      source_platform: params.metadata.sourcePlatform,
      dimension: params.metadata.aspectRatio,
    });

    const fileName =
      params.sanitizedFileName ??
      buildVideoFileName(
        params.metadata.storyTitle,
        params.metadata.videoTitle,
      );

    await downloadMedia({
      url: params.url,
      fileName,
      id: params.metadata.clipId,
    });

    return ok(undefined);
  } catch (error) {
    logger.error('Download failed', extractErrorInfo(error));
    return err('EXTERNAL', 'Download failed', extractErrorInfo(error));
  }
}
